import { useCallback, useRef, useState } from 'react'
import { MeFormModel } from '../model'
import { usePageDataIsEdited } from '../../../hooks/usePageDataIsEdited'
import { useEditedFormData } from '../../../containers/SingleSheetV2/hooks/editedFormData'

export type OnChangeMe = <K extends keyof MeFormModel>(
  path: K,
  value: MeFormModel[K]
) => void

// 更新のみの画面のため新規作成の初期値は持たない。取得が完了するまでの
// プレースホルダで、取得に成功するまで画面には出ない。
const EMPTY_FORM_MODEL: MeFormModel = {
  code: '',
  iconUrl: '',
  name: '',
  nameKana: '',
  nameAlphabet: '',
  email: '',
  phoneNumber: '',
  birthday: undefined,
  timezoneId: undefined,
  division: undefined,
  role: undefined,
  permissionPolicies: '',
  hireLeaveDate: { startDate: undefined, endDate: undefined },
  expirationDate: { startDateTime: undefined, endDateTime: undefined },
  emailNotification: false,
  partnerMember: false,
  registrationStatus: undefined,
  oneTimeTokenExpiredAt: undefined,
  slackUser: undefined,
  githubUserName: '',
}

const isMeFormKey = (key: string): key is keyof MeFormModel =>
  key in EMPTY_FORM_MODEL

// ユーザー情報画面のフォーム状態管理。formModel の保持・各項目の onChange・
// 編集フラグ追跡のみを担い、取得/保存などのライフサイクルは data フックが持つ。
export const useMeFormState = () => {
  const [formModel, setFormModelState] = useState<MeFormModel>(EMPTY_FORM_MODEL)
  // submit から最新の formModel を deps に入れずに参照するための ref。保存中の入力を
  // 残すかどうかを再取得の直後に判定するため、描画を待たずに更新する。描画後に
  // 更新すると、再取得の完了と同じタイミングの入力を見落として上書きしてしまう。
  const formModelRef = useRef(formModel)
  const setFormModel = useCallback((next: MeFormModel) => {
    formModelRef.current = next
    setFormModelState(next)
  }, [])
  const { edited, editedPageData, refreshedPageData } = usePageDataIsEdited()
  const { editedFormData, editFormData, refreshEditedFormData } =
    useEditedFormData()

  const onChangeMe = useCallback<OnChangeMe>(
    (path, value) => {
      setFormModel({ ...formModelRef.current, [path]: value })
      editedPageData()
      editFormData(path)
    },
    [setFormModel, editedPageData, editFormData]
  )

  // 保存を始めてから再取得が終わるまでに入力された項目だけを編集済みにし直し、
  // その項目を返す。onChangeMe は変更した項目以外の値の参照を引き継ぐため、保存した
  // 内容から参照が変わった項目がその間に入力された項目になる。保存した項目の
  // マーカーまで残すと、差分に載らないのに画面上だけ未保存に見えてしまう。
  const markEditsSince = useCallback(
    (submitted: MeFormModel): (keyof MeFormModel)[] => {
      const current = formModelRef.current
      const editedKeys = Object.keys(current)
        .filter(isMeFormKey)
        .filter(key => current[key] !== submitted[key])
      refreshEditedFormData()
      if (editedKeys.length === 0) refreshedPageData()
      editedKeys.forEach(key => editFormData(key))
      return editedKeys
    },
    [refreshedPageData, refreshEditedFormData, editFormData]
  )

  return {
    formModel,
    setFormModel,
    formModelRef,
    onChangeMe,
    edited,
    refreshedPageData,
    editedFormData,
    refreshEditedFormData,
    markEditsSince,
  }
}
